import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type {
  BidLimitCell,
  BidLimitSegmentMeta,
  FeeAutoConsiderPolicy,
  MarketOrderPolicyEntry,
} from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'
import { useTranslation } from 'react-i18next'

import { Box } from '@enechain/ecloud-designsystem'

import { CardAccordion } from '~/pages/AdminBidLimits/components/CardAccordion'
import { EditingBadge } from '~/pages/AdminBidLimits/components/EditingBadge'
import type {
  BidLimitValue,
  MatrixDraft,
} from '~/pages/AdminBidLimits/models/bidLimitMatrix'
import type { CellKey } from '~/pages/AdminBidLimits/models/cellKey'
import { BidLimitMatrixCard } from '~/pages/AdminBidLimits/tabs/SpotNormalTab/BidLimitMatrixCard'
import { FeeAutoConsiderPolicyCard } from '~/pages/AdminBidLimits/tabs/SpotNormalTab/FeeAutoConsiderPolicyCard'
import { MarketOrderPolicyCard } from '~/pages/AdminBidLimits/tabs/SpotNormalTab/MarketOrderPolicyCard'

type Props = {
  cells: BidLimitCell[]
  marketOrderPolicyEntries: MarketOrderPolicyEntry[]
  feeAutoConsiderPolicy: FeeAutoConsiderPolicy | undefined
  /** JEPX 会員情報から取得した現在のスポット市場手数料単価。 無いときは undefined (`resolveFeeUnitPrice`)。 */
  feeUnitPrice: Decimal | undefined
  segmentMeta: BidLimitSegmentMeta | undefined
  draft: MatrixDraft
  saved: MatrixDraft
  /** マトリクスの編集モード (page-level の `useBidLimitMatrixDraft` が持つ) */
  isEditing: boolean
  onStartEdit: () => void
  onSetCell: (key: CellKey, value: BidLimitValue | undefined) => void
  onReset: () => void
  onCommit: (newDraft: MatrixDraft) => void
  /**
   * workflow-back-bar-readonly AC-17: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき市場価格注文ポリシートグル・マトリクス保存/リセットを disabled にする。
   */
  isReadonly?: boolean
}

export const SpotNormalTab = ({
  cells: _cells,
  marketOrderPolicyEntries,
  feeAutoConsiderPolicy,
  feeUnitPrice,
  segmentMeta,
  draft,
  saved,
  isEditing,
  onStartEdit,
  onSetCell,
  onReset,
  onCommit,
  isReadonly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Box>
      {/* カードの器・開閉標は `CardAccordion` に集約した。 8080 `.ecl-accordion`
          (bid-limits.html の各カードも同 class) 準拠で、 2026-08-31 の
          アコーディオン基準統一 (承認フロー ProcessAccordion を基準とする
          デザイナー判断) に従う。 */}
      {/* カードの並び順は spec `bidding/bid-limits.md`「ページ構成」の表に従う (#7030) */}
      {/* Card: 入札制限 matrix */}
      <CardAccordion
        marginBottom={4}
        badge={isEditing ? <EditingBadge /> : undefined}
        title={t('page:adminBidLimits.matrix.title')}
      >
        <BidLimitMatrixCard
          draft={draft}
          isEditing={isEditing}
          isReadonly={isReadonly}
          onCommit={onCommit}
          onReset={onReset}
          onSetCell={onSetCell}
          onStartEdit={onStartEdit}
          saved={saved}
          segment={BidSegment.SPOT_NORMAL}
          segmentMeta={segmentMeta}
        />
      </CardAccordion>

      {/* Card: 成行注文 許可 */}
      <CardAccordion
        marginBottom={4}
        title={t('page:adminBidLimits.marketOrder.title')}
      >
        <MarketOrderPolicyCard
          entries={marketOrderPolicyEntries}
          isReadonly={isReadonly}
          segment={BidSegment.SPOT_NORMAL}
        />
      </CardAccordion>

      {/* Card: 手数料自動考慮 */}
      <CardAccordion title={t('page:adminBidLimits.feeAutoConsider.titleSpot')}>
        <FeeAutoConsiderPolicyCard
          feeUnitPrice={feeUnitPrice}
          isReadonly={isReadonly}
          policy={feeAutoConsiderPolicy}
          segment={BidSegment.SPOT_NORMAL}
        />
      </CardAccordion>
    </Box>
  )
}
